import { useMemo } from 'react';
import { SpreadsheetImport } from '@/components/form/spreadsheet-import';
import { MaskedInput } from '@/components/form/masked-input';
import { DataTable } from '@/components/data-table/data-table';
import { columnsFor } from '@/components/data-table/features';
import { MoneyValue } from '@/components/money-value';
import { QueryState } from '@/components/query-state';
import { LoadingState } from '@/components/states';
import { StatusBadge } from '@/components/status-badge';
import { Page } from '@/components/shell/page';
import { Field } from '@/components/form/field';
import { FormError } from '@/components/form/form-error';
import { SelectField } from '@/components/form/select-field';
import { optionsFromLabels } from '@/components/form/select-options';
import { SwitchField } from '@/components/form/switch-field';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/features/auth/use-auth';
import { formatMoney, formatPercent } from '@/lib/money';
import { toast } from '@/lib/toast';
import {
  type CurrentTaxRate,
  type FiscalProfile,
  type EffectiveRateRow,
  type RevenueMonth,
  REGIME_LABELS,
  type TaxRegime,
  downloadRevenueTemplate,
  useFiscalProfile,
  useImportRevenue,
  useRates,
  useRevenue,
} from '../api/fiscal';
import { type ProfileDraft, useProfileDraft } from '../use-profile-draft';

const REGIME_OPTIONS = optionsFromLabels<TaxRegime>(REGIME_LABELS);
const ANNEX_OPTIONS = optionsFromLabels<'I' | 'II'>({
  I: 'Anexo I — Comércio',
  II: 'Anexo II — Indústria',
});

function monthLabel(competence: string): string {
  const [year, month] = competence.split('-');
  return `${month}/${year}`;
}

export function FiscalPage() {
  const { can } = useAuth();
  const canEdit = can('organization.manage');
  const profile = useFiscalProfile();

  return (
    <Page>
      <PageHeader
        title="Perfil fiscal"
        description="Regime tributário e a alíquota que entra no cálculo de preço e margem."
      />
      <CurrentRateCard />
      <QueryState query={profile} loading={<LoadingState variant="block" />}>
        {/* A chave recria o rascunho quando uma versão nova é salva: ele nasce do perfil salvo. */}
        {(data) => (
          <FiscalEditor
            key={data.profile?.validFrom ?? 'novo'}
            saved={data.profile}
            canEdit={canEdit}
          />
        )}
      </QueryState>
    </Page>
  );
}

/** Formulário e importação dividem o mesmo rascunho do perfil (ver `useProfileDraft`). */
function FiscalEditor({ saved, canEdit }: { saved: FiscalProfile | null; canEdit: boolean }) {
  const draft = useProfileDraft(saved);
  return (
    <>
      {canEdit && <ProfileForm draft={draft} />}
      <RevenueCard canEdit={canEdit} draft={draft} />
    </>
  );
}

function CurrentRateCard() {
  const profile = useFiscalProfile();
  if (profile.isPending) return <Skeleton className="h-24 w-full" />;
  if (!profile.data) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Alíquota usada hoje</CardTitle>
      </CardHeader>
      <CardContent>
        <RateExplanation rate={profile.data.currentRate} />
      </CardContent>
    </Card>
  );
}

function RateExplanation({ rate }: { rate: CurrentTaxRate }) {
  if (rate.status === 'MISSING') {
    return (
      <p className="text-sm text-warning-ink">
        Sem alíquota: {rate.reason}. As simulações ficam incompletas até isso ser resolvido.
      </p>
    );
  }
  return (
    <div className="space-y-1 text-sm">
      <p>
        <span className="tabular text-2xl font-semibold">{formatPercent(rate.rate)}</span>{' '}
        <StatusBadge tone={rate.source === 'CALCULATED' ? 'success' : 'neutral'}>
          {rate.source === 'CALCULATED' ? 'calculada' : 'informada'}
        </StatusBadge>
      </p>
      {rate.detail && (
        <p className="text-muted-foreground">
          Competência {monthLabel(rate.competence)} · RBT12 {formatMoney(rate.detail.rbt12)} ·{' '}
          {rate.detail.band}ª faixa · tabela {rate.detail.tableVersion}
        </p>
      )}
      {rate.carriedReason && (
        <p className="text-warning-ink">
          Repetindo a última alíquota calculada porque {rate.carriedReason}. Importe o faturamento
          que falta para atualizar.
        </p>
      )}
      {rate.fallbackReason && (
        <p className="text-warning-ink">
          Usando o percentual informado porque {rate.fallbackReason}.
        </p>
      )}
    </div>
  );
}

function ProfileForm({ draft }: { draft: ProfileDraft }) {
  const { form, isSimples, useCalculated } = draft;
  const submit = form.handleSubmit(async (input) => {
    const saved = await draft
      .save(input)
      .then(() => true)
      .catch(() => false);
    if (saved) toast.success('Perfil fiscal salvo. Uma nova versão foi criada.');
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Regime tributário</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
          <FormError
            error={draft.saveError}
            fallback="Não foi possível salvar."
            className="sm:col-span-2"
          />
          <SelectField
            control={form.control}
            name="regime"
            label="Regime"
            required
            options={REGIME_OPTIONS}
          />
          {isSimples && (
            <SelectField
              control={form.control}
              name="annex"
              label="Anexo"
              required
              hint="Quem revende mercadoria costuma estar no Anexo I."
              options={ANNEX_OPTIONS}
            />
          )}
          {isSimples && (
            <SwitchField
              control={form.control}
              name="calculated"
              className="sm:col-span-2"
              label="Calcular a alíquota efetiva automaticamente"
              description="Pelo faturamento dos últimos 12 meses."
            />
          )}
          <Field
            control={form.control}
            name="manual"
            label={useCalculated ? 'Percentual de reserva (%)' : 'Percentual efetivo (%)'}
            required={!useCalculated}
            hint={
              useCalculated
                ? 'Usado enquanto não houver 12 meses de faturamento. Opcional.'
                : 'Alíquota efetiva sobre a venda, ex.: 6 ou 6,5.'
            }
          >
            {(field) => <MaskedInput {...field} mask="percent" />}
          </Field>
          <div className="flex items-end">
            <Button type="submit" disabled={draft.isSaving}>
              {draft.isSaving ? 'Salvando…' : 'Salvar perfil'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function RevenueCard({ canEdit, draft }: { canEdit: boolean; draft: ProfileDraft }) {
  const revenue = useRevenue();
  const rates = useRates();
  const importRevenue = useImportRevenue();

  async function importFile(file: File) {
    const result = await importRevenue.mutateAsync(file);
    const months = `${result.imported} ${result.imported === 1 ? 'mês importado' : 'meses importados'}`;
    // Sem perfil salvo o servidor não calcula: salva o da tela junto com a importação.
    const profile = await draft.saveIfNeeded();
    if (profile === 'saved') {
      toast.success(`${months} e perfil fiscal salvo. Alíquota calculada.`);
    } else if (profile === 'unchanged') {
      toast.success(`${months}. Alíquota recalculada.`);
    } else {
      toast.warning(
        `${months}, mas a alíquota ainda não foi calculada: ${profile.missing} Complete o regime tributário e salve.`,
      );
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Faturamento mensal</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {canEdit && (
          <SpreadsheetImport
            label="Planilha de faturamento"
            hint="Peça ao contador o faturamento dos últimos 12 meses no modelo (colunas competência e faturamento)."
            pending={importRevenue.isPending}
            onDownload={downloadRevenueTemplate}
            onImport={importFile}
          />
        )}

        <QueryState
          query={revenue}
          loading={<LoadingState rows={3} />}
          isEmpty={(data) => data.length === 0}
          empty={<p className="text-sm text-muted-foreground">Nenhum faturamento informado.</p>}
        >
          {(data) => <RevenueTable months={data} rates={rates.data ?? NO_RATES} />}
        </QueryState>
      </CardContent>
    </Card>
  );
}

const NO_RATES: EffectiveRateRow[] = [];
const revenueColumn = columnsFor<RevenueMonth>();

/** Faturamento por mês e a alíquota efetiva que ele produz (Simples Nacional). */
function RevenueTable({ months, rates }: { months: RevenueMonth[]; rates: EffectiveRateRow[] }) {
  const columns = useMemo(() => {
    const rateByMonth = new Map(rates.map((row) => [row.competence, row]));
    return revenueColumn.columns([
      revenueColumn.accessor('competence', {
        header: 'Competência',
        cell: ({ getValue }) => monthLabel(getValue()),
      }),
      revenueColumn.accessor('grossRevenue', {
        header: 'Faturamento',
        sortFn: 'decimal',
        meta: { align: 'right' },
        cell: ({ getValue }) => <MoneyValue value={getValue()} />,
      }),
      revenueColumn.accessor((month) => rateByMonth.get(month.competence)?.effectiveRate, {
        id: 'rate',
        header: 'Alíquota efetiva',
        sortFn: 'decimal',
        meta: { align: 'right' },
        cell: ({ row }) => {
          const rate = rateByMonth.get(row.original.competence);
          return rate ? `${formatPercent(rate.effectiveRate)} (${rate.band}ª faixa)` : '—';
        },
      }),
    ]);
  }, [rates]);
  return <DataTable data={months} columns={columns} getRowId={(month) => month.competence} />;
}
